import { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Button,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import PhotoFilterIcon from "@mui/icons-material/PhotoFilter"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import { DateTime } from "luxon"

import { AnimatedBadge } from "widgets/styled/widgets"
import { formatReleaseDate } from "helpers/utils/dateUtils"
import { changelogEntriesEs } from "./entries.es"
import { changelogEntriesEn } from "./entries.en"
import Change from "./Change"

const ChangelogMenu = () => {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage

  const lastEntry = useMemo(() => {
    const langEntries = lang === "es" ? changelogEntriesEs : changelogEntriesEn
    return langEntries[0]
  }, [lang])

  const formattedReleaseDate = useMemo(
    () => formatReleaseDate(lastEntry.release, lang || "en"),
    [lastEntry.release, lang],
  )

  const showNewBadge = useMemo(() => {
    const releaseDate = DateTime.fromISO(lastEntry.release)
    const now = DateTime.now()
    const daysSinceRelease = now.diff(releaseDate, "days").days
    return daysSinceRelease <= 2
  }, [lastEntry.release])

  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)

  const handleOpen = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }

  return (
    <Stack mr={showNewBadge ? 2 : 1}>
      <Tooltip title={t("changelog.CHANGELOG")}>
        <AnimatedBadge badgeContent={t("changelog.NEW")} invisible={!showNewBadge}>
          <IconButton ref={anchorRef} onClick={handleOpen}>
            <PhotoFilterIcon />
          </IconButton>
        </AnimatedBadge>
      </Tooltip>
      <Popover
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.8,
              ml: 0.5,
              overflow: "inherit",
              width: 450,
            },
          },
        }}
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
      >
        <Stack>
          <Typography
            variant="h4"
            p={2}
            borderBottom={1}
            borderColor={(theme) => theme.palette.neutral[400]}
          >
            {t("changelog.RECENT_CHANGES")}:{" "}
            <Typography component={"span"} variant="h4" fontWeight={"normal"}>
              {formattedReleaseDate}
            </Typography>
          </Typography>
          <Stack maxHeight={300} spacing={2} overflow={"auto"} pt={2}>
            {lastEntry.changes.map((change) => (
              <Stack key={change.title}>
                <Change change={change} variant="compact" />
                <Divider />
              </Stack>
            ))}
          </Stack>
          <Stack
            direction="row"
            justifyContent="center"
            p={1}
            borderTop={1}
            borderColor={(theme) => theme.palette.neutral[400]}
          >
            <Button
              href={"/changelog"}
              onClick={handleClose}
              target="_blank"
              size="small"
              endIcon={<OpenInNewIcon />}
            >
              {t("changelog.SEE_FULL_CHANGELOG")}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  )
}

export default ChangelogMenu
